import { 
  Assessment, 
  AssessmentGoal, 
  AssessmentResult, 
  RewardTier, 
  RewardTierLabel,
  PenaltyTier,
  OutcomeType,
  ArtifactReward
} from '../types/assessmentGoals';

/**
 * Assessment Goals Utility Functions
 * 
 * Core logic for computing rewards/penalties based on goal vs actual scores.
 */

// ============================================================================
// Reward/Penalty Computation
// ============================================================================

/**
 * Computes the PP change based on goal score, actual score, and assessment configuration.
 * 
 * @param goalScore - The student's goal score
 * @param actualScore - The actual score achieved
 * @param assessment - The assessment configuration with reward/penalty tiers
 * @returns Object with outcome, ppChange, and tier explanation
 */
export function computePPChange(
  goalScore: number,
  actualScore: number,
  assessment: Assessment
): {
  outcome: OutcomeType;
  ppChange: number;
  tierExplanation: string;
  absDiff: number;
  delta: number;
  artifactsGranted?: ArtifactReward[];
} {
  const delta = actualScore - goalScore;
  const absDiff = Math.abs(delta);
  
  // Check if actual score meets minimum goal score requirement (if set)
  const minGoalScore = assessment.minGoalScore ?? 0;
  const meetsMinimumRequirement = actualScore >= minGoalScore;
  
  // Determine outcome and matching tier
  let matchingRewardTier: RewardTier | undefined;
  let outcome: OutcomeType;
  let matchingTierLabel: 'Completed' | 'Almost' | 'Attempted' | 'Did not Complete' | undefined;
  
  const isWeeklyDeliverable = assessment.type === 'weekly_deliverable';
  // Check if this is a Story Goal (should use label-based tiers)
  const isStoryGoal = assessment.type === 'story-goal';
  const hasLabelBasedTiers = assessment.rewardTiers.some(tier => tier.label !== undefined);
  
  if (isWeeklyDeliverable) {
    const target = Math.max(1, assessment.maxScore || 100);
    if (actualScore >= target) {
      matchingTierLabel = 'Completed';
      outcome = 'hit';
    } else {
      matchingTierLabel = 'Did not Complete';
      outcome = 'miss';
    }
    matchingRewardTier = assessment.rewardTiers.find((tier) => tier.label === matchingTierLabel);
  } else if (isStoryGoal && hasLabelBasedTiers) {
    // Story Goals: Use label-based system
    if (actualScore >= goalScore) {
      // Met or exceeded goal
      matchingTierLabel = 'Completed';
      outcome = actualScore > goalScore ? 'exceed' : 'hit';
    } else if (absDiff <= 2 && meetsMinimumRequirement) {
      // Close but didn't meet (within 2 points and meets minimum)
      matchingTierLabel = 'Almost';
      outcome = 'hit';
    } else if (actualScore > 0 && meetsMinimumRequirement) {
      // Showed up and attempted but far from goal
      matchingTierLabel = 'Attempted';
      outcome = 'miss';
    } else {
      // Didn't complete or very low score
      matchingTierLabel = 'Did not Complete';
      outcome = 'miss';
    }
    
    // Find the matching reward tier by label
    matchingRewardTier = assessment.rewardTiers.find(tier => tier.label === matchingTierLabel);
  } else {
    // Other assessment types: threshold-based rewards only when meeting/exceeding the goal.
    // Below goal uses missPenaltyTiers (reward tiers describe how close above/at goal is, not consolation pay).
    if (actualScore >= goalScore) {
      outcome = actualScore > goalScore ? 'exceed' : 'hit';
      const sortedTiers = [...assessment.rewardTiers].sort((a, b) => (a.threshold || 0) - (b.threshold || 0));
      matchingRewardTier = sortedTiers.find((tier) => absDiff <= (tier.threshold ?? Infinity));
      if (!matchingRewardTier && sortedTiers.length > 0) {
        matchingRewardTier = sortedTiers[sortedTiers.length - 1];
      }
    } else {
      outcome = 'miss';
    }
  }
  
  let ppChange = 0;
  let tierExplanation = '';
  
  if (matchingRewardTier) {
    if (outcome === 'hit' || outcome === 'exceed') {
      ppChange = matchingRewardTier.bonus;
      if (isWeeklyDeliverable && matchingTierLabel) {
        tierExplanation = `${matchingTierLabel} — ${matchingRewardTier.bonus} PP`;
      } else if (isStoryGoal && matchingTierLabel) {
        tierExplanation = `${matchingTierLabel} - ${matchingRewardTier.bonus} PP reward`;
      } else {
        if (absDiff === 0) {
          tierExplanation = 'Exact hit bonus';
        } else if (actualScore > goalScore) {
          tierExplanation = `${absDiff} point(s) over goal - ${matchingRewardTier.threshold} point tier reward`;
        } else {
          tierExplanation = `${absDiff} point(s) under goal (within ${matchingRewardTier.threshold} point tier) - reward applied`;
        }
      }
    } else {
      // For "Attempted" or "Did not Complete", still use the tier's bonus (which might be 0 or negative)
      ppChange = matchingRewardTier.bonus;
      if (isStoryGoal && matchingTierLabel) {
        tierExplanation = `${matchingTierLabel} - ${ppChange > 0 ? '+' : ''}${ppChange} PP`;
      } else {
        tierExplanation = `Miss - ${ppChange > 0 ? '+' : ''}${ppChange} PP`;
      }
    }
  } else {
    // No matching tier found, use default behavior
    if (outcome === 'hit' || outcome === 'exceed') {
      ppChange = 0;
      tierExplanation = 'No reward tier configured';
    } else {
      // Apply penalty based on miss penalty tiers
      const penaltyTier = findPenaltyTier(absDiff, assessment.missPenaltyTiers);
      if (penaltyTier) {
        ppChange = -penaltyTier.penalty; // Negative for penalty
        tierExplanation = `Within ${penaltyTier.threshold} points off (penalty)`;
      } else {
        const worstTier = assessment.missPenaltyTiers[assessment.missPenaltyTiers.length - 1];
        ppChange = -Math.min(worstTier?.penalty || 0, assessment.penaltyCap || 75);
        tierExplanation = `Penalty (capped at ${assessment.penaltyCap})`;
      }
    }
  }
  
  // Apply bonus cap
  if (assessment.bonusCap && ppChange > 0) {
    ppChange = Math.min(ppChange, assessment.bonusCap);
  }
  
  // Apply penalty cap
  if (assessment.penaltyCap && ppChange < 0) {
    ppChange = Math.max(ppChange, -assessment.penaltyCap);
  }
  
  // Get artifacts for the matching tier
  let artifactsGranted: ArtifactReward[] | undefined;
  if (matchingRewardTier && matchingRewardTier.artifacts && matchingRewardTier.artifacts.length > 0) {
    artifactsGranted = matchingRewardTier.artifacts;
  }

  return {
    outcome,
    ppChange,
    tierExplanation,
    absDiff,
    delta,
    artifactsGranted
  };
}

/**
 * Finds the appropriate reward tier by label.
 * This function is kept for backward compatibility but is no longer used
 * since we now use labels instead of thresholds.
 * @deprecated Use label-based matching instead
 */
function findRewardTier(absDiff: number, tiers: RewardTier[]): RewardTier | null {
  // This function is deprecated - tier matching is now done by label in computePPChange
  return null;
}

/**
 * Finds the appropriate penalty tier for a given absolute difference.
 * Returns the tier with the smallest threshold where absDiff <= threshold.
 */
function findPenaltyTier(absDiff: number, tiers: PenaltyTier[]): PenaltyTier | null {
  if (!tiers || tiers.length === 0) return null;
  
  // Sort tiers by threshold ascending
  const sortedTiers = [...tiers].sort((a, b) => a.threshold - b.threshold);
  
  // Find the smallest threshold where absDiff <= threshold
  for (const tier of sortedTiers) {
    if (absDiff <= tier.threshold) {
      return tier;
    }
  }
  
  // No tier matches, return null (caller will use worst tier)
  return null;
}

// ============================================================================
// Validation Functions
// ============================================================================

/**
 * Validates that a goal score is within valid range (minGoalScore to maxScore).
 */
export function validateGoalScore(goalScore: number, maxScore: number, minGoalScore: number = 0): {
  valid: boolean;
  error?: string;
} {
  if (isNaN(goalScore)) {
    return { valid: false, error: 'Goal score must be a number' };
  }
  
  if (goalScore < minGoalScore) {
    return { valid: false, error: `Goal score cannot be less than minimum (${minGoalScore})` };
  }
  
  if (goalScore > maxScore) {
    return { valid: false, error: `Goal score cannot exceed maximum score (${maxScore})` };
  }
  
  return { valid: true };
}

/**
 * Validates assessment configuration (tiers, caps, etc.).
 */
export function validateAssessmentConfig(assessment: Partial<Assessment>): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];
  
  if (assessment.maxScore === undefined || assessment.maxScore <= 0) {
    errors.push('Max score must be a positive number');
  }
  
  if (assessment.minGoalScore !== undefined) {
    if (assessment.minGoalScore < 0) {
      errors.push('Minimum goal score cannot be negative');
    }
    if (assessment.maxScore !== undefined && assessment.minGoalScore > assessment.maxScore) {
      errors.push('Minimum goal score cannot exceed max score');
    }
  }
  
  if (!assessment.rewardTiers || assessment.rewardTiers.length === 0) {
    errors.push('At least one reward tier is required');
  } else {
    // Check that tiers are valid
    assessment.rewardTiers.forEach((tier, index) => {
      // Validate label-based tiers (new system)
      if (tier.label !== undefined) {
        if (tier.bonus < 0) {
          errors.push(`Reward tier ${index + 1} (${tier.label}): bonus cannot be negative`);
        }
      } else if (tier.threshold !== undefined) {
        // Validate threshold-based tiers (legacy system)
        if (tier.threshold < 0) {
          errors.push(`Reward tier ${index + 1}: threshold cannot be negative`);
        }
        if (tier.bonus < 0) {
          errors.push(`Reward tier ${index + 1}: bonus cannot be negative`);
        }
      } else {
        errors.push(`Reward tier ${index + 1}: must have either a label or threshold`);
      }
    });
  }
  
  if (!assessment.missPenaltyTiers || assessment.missPenaltyTiers.length === 0) {
    errors.push('At least one penalty tier is required');
  } else {
    // Check that tiers are valid
    assessment.missPenaltyTiers.forEach((tier, index) => {
      if (tier.threshold < 0) {
        errors.push(`Penalty tier ${index + 1}: threshold cannot be negative`);
      }
      if (tier.penalty < 0) {
        errors.push(`Penalty tier ${index + 1}: penalty cannot be negative`);
      }
    });
  }
  
  if (assessment.bonusCap !== undefined && assessment.bonusCap < 0) {
    errors.push('Bonus cap cannot be negative');
  }
  
  if (assessment.penaltyCap !== undefined && assessment.penaltyCap < 0) {
    errors.push('Penalty cap cannot be negative');
  }

  const refType = assessment.type;
  if (refType === 'weekly_deliverable') {
    const t = assessment.weeklyDeliverableConfig?.assignmentType?.trim();
    if (!t) {
      errors.push('Weekly Deliverable: describe the assignment type (what students must turn in or complete).');
    }
    const labels = new Set(
      (assessment.rewardTiers || []).map((x) => x.label).filter(Boolean) as RewardTierLabel[]
    );
    if (!labels.has('Completed')) {
      errors.push('Weekly Deliverable: add a reward tier labeled "Completed".');
    }
    if (!labels.has('Did not Complete')) {
      errors.push('Weekly Deliverable: add a reward tier labeled "Did not Complete" (PP is often 0).');
    }
  }

  if (refType === 'reflection' || refType === 'live_reflection') {
    const qs = assessment.reflectionConfig?.questions;
    if (!qs || qs.length === 0) {
      errors.push('Reflection assessments need at least one question.');
    } else {
      qs.forEach((qu, index) => {
        if (!qu.prompt || !String(qu.prompt).trim()) {
          errors.push(`Reflection question ${index + 1}: prompt is required.`);
        }
        if (qu.responseMode === 'preset') {
          const opts = (qu.presetOptions || []).map((s) => String(s).trim()).filter(Boolean);
          if (opts.length < 2) {
            errors.push(
              `Reflection question ${index + 1}: preset mode needs at least two non-empty options.`
            );
          }
        }
      });
    }
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Generates a unique goal document ID: ${assessmentId}_${studentId}
 */
export function generateGoalId(assessmentId: string, studentId: string): string {
  return `${assessmentId}_${studentId}`;
}

/**
 * Generates a unique result document ID: ${assessmentId}_${studentId}
 */
export function generateResultId(assessmentId: string, studentId: string): string {
  return `${assessmentId}_${studentId}`;
}

/**
 * Formats the outcome type for display.
 */
export function formatOutcome(outcome: OutcomeType): string {
  switch (outcome) {
    case 'hit':
      return 'Hit Goal';
    case 'exceed':
      return 'Exceeded Goal';
    case 'miss':
      return 'Missed Goal';
    default:
      return outcome;
  }
}

/**
 * Formats PP change for display (with + or - sign).
 */
export function formatPPChange(ppChange: number): string {
  if (ppChange > 0) {
    return `+${ppChange}`;
  } else if (ppChange < 0) {
    return `${ppChange}`;
  } else {
    return '0';
  }
}

